import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CreateHotelServiceFormData,
  createHotelServiceSchema,
  defaultServiceDisplaySettings,
  mergeServiceDisplaySettings,
  ServiceDisplaySettingsForm,
} from "../utils/serviceSchema";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useHotel } from "@/hooks/useHotel";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { TimePicker } from "@/components/common/TimePicker";
import { PhonePreview } from "@/components/common/PhonePreview";
import ServiceCategoryField from "../components/ServiceCategoryField";
import ServiceProviderTypeField from "../components/ServiceProviderTypeField";
import { FormField } from "../../reservations/guests/components/form/FormFields";
import { useAmenities } from "@/components/pages/amenities/hooks/amenities.hooks";
import ImageSelector from "../../reservations/guests/components/form/ImageSelector";

import {
  HotelServiceAutoCreateTrigger,
  HotelServiceCategory,
  HotelServiceProviderType,
  HotelServiceStatus,
  WeekDay,
} from "@/types/hotelService";
import {
  ChevronDown,
  ChevronUp,
  Image,
  PencilLine,
  PlusCircle,
} from "lucide-react";
import AssignStaffOrTeamCell from "./AssignStaffOrTeamCell";
import { getPercentageOf } from "@/components/helpers/functions";

interface Props {
  initialValues?: Partial<CreateHotelServiceFormData>;
  onSubmit: (
    data: CreateHotelServiceFormData,
    selectedImage: File | null,
  ) => Promise<void>;
  isSubmitting: boolean;
  submitButtonText?: string;
  titleText: string;
}

const NONE_VALUE = "__none__";

enum ServiceFormTab {
  DETAILS = "details",
  DISPLAY = "display",
}

const serviceFormTabsListClassName =
  "mx-auto h-9 w-full max-w-md items-stretch gap-1 bg-secondary-badge p-1 text-black shadow-xs";
const serviceFormTabsTriggerClassName =
  "h-full flex-1 rounded-md border border-transparent px-3 py-1 text-xs font-medium shadow-none data-[state=active]:border-custom-outline data-[state=active]:bg-royal-glow-5 data-[state=active]:text-black data-[state=active]:shadow-none";

const DISPLAY_STAGE_KEYS = [
  "preCheckIn",
  "inHouse",
  "preCheckOut",
] as const satisfies ReadonlyArray<
  keyof ServiceDisplaySettingsForm["carousel"]
>;

type DisplayFeatureRowKey = keyof Pick<
  ServiceDisplaySettingsForm,
  "checkInCheckOut" | "carousel" | "specialServices" | "ctaList" | "chatDisplay"
>;

const AUTO_CREATE_TRIGGER_KEYS = [
  HotelServiceAutoCreateTrigger.BEFORE_CHECKIN,
  HotelServiceAutoCreateTrigger.DAILY_IN_HOUSE,
  HotelServiceAutoCreateTrigger.AFTER_DEPARTURE,
] as const;

const HotelServiceForm: React.FC<Props> = ({
  initialValues,
  onSubmit,
  isSubmitting,
  submitButtonText = "Submit",
  titleText,
}) => {
  const { t } = useTranslation();
  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null);
  const [isSale, setIsSale] = useState<boolean>(false);
  const [isContractorSectionOpen, setIsContractorSectionOpen] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
    clearErrors,
    reset,
  } = useForm<CreateHotelServiceFormData>({
    resolver: zodResolver(createHotelServiceSchema),
    defaultValues: {
      availableDays: [],
      price: "0",
      isRecommended: true,
      status: HotelServiceStatus.ACTIVE,
      ...initialValues,
      autoCreateTriggers: initialValues?.autoCreateTriggers ?? [],
      serviceDisplaySettings: mergeServiceDisplaySettings(
        initialValues?.serviceDisplaySettings,
      ),
    },
  });

  const name = watch("name");
  const price = watch("price");
  const description = watch("description");
  const selectedImage = watch("photoId");
  const categoryId = watch("categoryId");
  const providerType = watch("providerType");
  const availableDays = watch("availableDays");
  const isRecommended = watch("isRecommended");
  const autoCreateTriggers = watch("autoCreateTriggers") ?? [];
  const availableFromTime = watch("availableFromTime");
  const availableToTime = watch("availableToTime");
  const amenityId = watch("amenityId");
  const staffId = watch("staffId");
  const teamId = watch("teamId");
  const discount = watch("discount");
  const serviceDisplaySettings = watch("serviceDisplaySettings");

  const { hotelId } = useHotel();
  const { data: amenitiesResponse, isLoading: isAmenitiesLoading } =
    useAmenities({ hotelId });
  const amenities = amenitiesResponse?.items ?? [];

  useEffect(() => {
    if (initialValues) {
      reset({
        ...initialValues,
        autoCreateTriggers: initialValues.autoCreateTriggers ?? [],
        serviceDisplaySettings: mergeServiceDisplaySettings(
          initialValues.serviceDisplaySettings,
        ),
      });
      setIsSale(!!initialValues.discount);
    }
  }, [initialValues, reset]);

  useEffect(() => {
    if (providerType !== HotelServiceProviderType.EXTERNAL_SERVICE) {
      clearErrors("contractor");
      setValue("contractor", undefined);
    }
  }, [providerType, setValue, clearErrors]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setValue("photoId", imageUrl);
      setSelectedPhotoFile(file);
    }
  };

  const handleChangeAvailableDays = (day: WeekDay, checked: boolean) => {
    setValue(
      "availableDays",
      checked
        ? [...availableDays, day]
        : availableDays.filter((d) => d !== day),
    );
  };

  const handleAmenityChange = (value: string) => {
    setValue("amenityId", value === NONE_VALUE ? undefined : value);
  };

  const handleAutoCreateTriggerChange = (
    trigger: HotelServiceAutoCreateTrigger,
    checked: boolean,
  ) => {
    const nextTriggers = checked
      ? [...autoCreateTriggers, trigger]
      : autoCreateTriggers.filter((value) => value !== trigger);

    setValue("autoCreateTriggers", Array.from(new Set(nextTriggers)));
  };

  const setGuestStageVisibility = (
    feature: DisplayFeatureRowKey,
    stage: (typeof DISPLAY_STAGE_KEYS)[number],
    checked: boolean,
  ) => {
    const current = getValues("serviceDisplaySettings");
    setValue("serviceDisplaySettings", {
      ...current,
      [feature]: {
        ...current[feature],
        [stage]: checked,
      },
    });
  };

  const handleSubmitService: SubmitHandler<CreateHotelServiceFormData> = async (
    data,
  ) => {
    try {
      await onSubmit(data, selectedPhotoFile);
    } catch (error) {
      console.error("Error submitting service:", error);
    }
  };

  return (
    <div className="mt-10 flex">
      <div className="flex-1 border-r-1 pr-6">
        <h1 className="mb-4 text-center text-2xl font-semibold">{titleText}</h1>
        <form
          onSubmit={handleSubmit(handleSubmitService)}
          className="space-y-6"
        >
          <Tabs defaultValue={ServiceFormTab.DETAILS} className="w-full">
            <TabsList className={serviceFormTabsListClassName}>
              <TabsTrigger
                value={ServiceFormTab.DETAILS}
                className={serviceFormTabsTriggerClassName}
              >
                {t("services.form.tabs.details")}
              </TabsTrigger>
              <TabsTrigger
                value={ServiceFormTab.DISPLAY}
                className={serviceFormTabsTriggerClassName}
              >
                {t("services.form.tabs.display")}
              </TabsTrigger>
            </TabsList>

            <TabsContent
              value={ServiceFormTab.DETAILS}
              forceMount
              className="space-y-6 data-[state=inactive]:hidden"
            >
              <ImageSelector
                defaultIcon={<Image className="size-[72px] stroke-1" />}
                overlayContent={
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full bg-white">
                    <PlusCircle className="size-5" />
                  </div>
                }
                onImageSelect={handleImageUpload}
                selectedImage={selectedImage}
                className="mb-4"
                imageClassName="size-[72px] rounded-full"
              />

              <FormField
                id="name"
                label="NAME"
                required
                placeholder="Name"
                register={register}
                error={errors.name?.message}
                {...register("name")}
              />

              <div>
                <label className="text-sm font-medium text-gray-700 uppercase">
                  Description *
                </label>
                <Textarea
                  {...register("description")}
                  className="mt-1 min-h-[80px] w-full resize-none"
                  placeholder="Enter description"
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.description.message}
                  </p>
                )}
              </div>

              <FormField
                id="price"
                label="PRICE"
                type="number"
                placeholder="0"
                min={0}
                onChange={({ target }) => setValue("price", target.value)}
                required
                onKeyDown={(e) => {
                  if (e.key === "-") {
                    e.preventDefault();
                  }
                }}
                register={register}
                error={errors.price?.message}
              />

              <div className={"flex gap-4"}>
                <Switch
                  checked={isSale}
                  onCheckedChange={(checked: boolean) => {
                    setIsSale(checked);
                  }}
                />

                <label>Sale</label>
              </div>

              <FormField
                disabled={!isSale}
                id="discount"
                label="DISCOUNT"
                type="number"
                placeholder="0"
                onKeyDown={(e) => {
                  if (e.key === "-") {
                    e.preventDefault();
                  }
                }}
                min={0}
                onChange={({ target }) => setValue("discount", target.value)}
                required
                register={register}
                error={errors.price?.message}
              />

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isRecommended"
                  checked={isRecommended}
                  onCheckedChange={() =>
                    setValue("isRecommended", !isRecommended)
                  }
                />
                <label htmlFor={"isRecommended"} className="text-sm">
                  Is service recommended for guests?
                </label>
              </div>

              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 uppercase">
                    CATEGORIES
                  </label>
                  <ServiceCategoryField
                    withNullValue={false}
                    className="flex w-full justify-between"
                    value={categoryId}
                    onChange={(value) =>
                      setValue("categoryId", value as HotelServiceCategory)
                    }
                    error={errors.categoryId?.message}
                  />
                </div>

                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 uppercase">
                    AMENITY
                  </label>
                  <div className="mt-1">
                    <Select
                      value={amenityId ?? NONE_VALUE}
                      onValueChange={handleAmenityChange}
                    >
                      <SelectTrigger className="w-full justify-between">
                        <SelectValue
                          placeholder={
                            isAmenitiesLoading
                              ? "Loading amenities..."
                              : "Select amenity (optional)"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE_VALUE}>None</SelectItem>
                        {amenities.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 uppercase">
                    TYPE
                  </label>
                  <ServiceProviderTypeField
                    withNullValue={false}
                    className="flex w-full justify-between"
                    value={providerType}
                    onChange={(value) =>
                      setValue(
                        "providerType",
                        value as HotelServiceProviderType,
                      )
                    }
                    error={errors.providerType?.message}
                  />
                </div>
              </div>

              {providerType === HotelServiceProviderType.EXTERNAL_SERVICE && (
                <div className="space-y-4">
                  <div
                    className="flex cursor-pointer items-center justify-between rounded-md bg-gray-100 p-4"
                    onClick={() =>
                      setIsContractorSectionOpen(!isContractorSectionOpen)
                    }
                  >
                    <span className="text-sm font-medium">
                      {t(
                        "services.contractor.additionalInfo",
                        "Additional information about the external contractor",
                      )}
                    </span>
                    {isContractorSectionOpen ? <ChevronUp /> : <ChevronDown />}
                  </div>

                  {isContractorSectionOpen && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-medium">
                        {t(
                          "services.contractor.detailsTitle",
                          "External Contractor Details",
                        )}
                      </h2>
                      <div className="flex w-full gap-4">
                        <FormField
                          id="contractor.name"
                          label={t("services.contractor.name", "NAME")}
                          required
                          placeholder="Name"
                          containerClassName="w-full"
                          register={register}
                          error={errors.contractor?.name?.message}
                          {...register("contractor.name")}
                        />
                        <FormField
                          id="contractor.surname"
                          label={t("services.contractor.surname", "SURNAME")}
                          required
                          placeholder="Surname"
                          containerClassName="w-full"
                          register={register}
                          error={errors.contractor?.surname?.message}
                          {...register("contractor.surname")}
                        />
                      </div>

                      <div className="flex gap-4">
                        <FormField
                          id="contractor.phone"
                          label={t("services.contractor.phone", "PHONE NUMBER")}
                          required
                          placeholder="Number"
                          register={register}
                          containerClassName="w-full"
                          error={errors.contractor?.phone?.message}
                          {...register("contractor.phone")}
                        />
                        <FormField
                          id="contractor.email"
                          label={t("services.contractor.email", "EMAIL")}
                          required
                          placeholder="Email"
                          containerClassName="w-full"
                          register={register}
                          error={errors.contractor?.email?.message}
                          {...register("contractor.email")}
                        />
                      </div>

                      <FormField
                        id="contractor.fee"
                        label={t("services.contractor.fee", "CONTRACTOR'S FEE")}
                        type="number"
                        placeholder="0$"
                        required
                        register={register}
                        error={errors.contractor?.fee?.message}
                        {...register("contractor.fee")}
                        onChange={({ target }) =>
                          setValue("contractor.fee", target.value)
                        }
                      />
                    </div>
                  )}
                </div>
              )}

              <label className={"text-sm font-medium text-gray-700"}>
                Default Assigned
              </label>

              <AssignStaffOrTeamCell
                teamId={teamId}
                assignedStaffId={staffId}
                setAssignedStaffId={(s) => setValue("staffId", s)}
                setTeamId={(t) => setValue("teamId", t)}
              />

              <div className="border-border space-y-3 rounded-lg border p-4">
                <div className="space-y-1">
                  <h3 className="text-foreground text-base font-semibold">
                    {t("services.form.autoCreate.title")}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t("services.form.autoCreate.description")}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t("services.form.autoCreate.helper")}
                  </p>
                </div>

                <div className="space-y-3">
                  {AUTO_CREATE_TRIGGER_KEYS.map((trigger) => (
                    <label
                      key={trigger}
                      className="border-border flex items-start gap-3 rounded-md border px-3 py-2"
                    >
                      <Checkbox
                        checked={autoCreateTriggers.includes(trigger)}
                        onCheckedChange={(checked: boolean) =>
                          handleAutoCreateTriggerChange(trigger, checked)
                        }
                      />
                      <span className="space-y-1">
                        <span className="text-foreground block text-sm font-medium">
                          {t(
                            `services.form.autoCreate.triggers.${trigger}.title`,
                          )}
                        </span>
                        <span className="text-muted-foreground block text-xs">
                          {t(
                            `services.form.autoCreate.triggers.${trigger}.description`,
                          )}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <h3 className="text-2xl text-black">Availability</h3>
              <div className="flex gap-4">
                <TimePicker
                  id="availableFromTime"
                  label="FROM"
                  required
                  register={register}
                  max={availableToTime}
                  error={errors.availableFromTime?.message}
                />
                <TimePicker
                  id="availableToTime"
                  label="TO"
                  required
                  register={register}
                  min={availableFromTime}
                  error={errors.availableToTime?.message}
                />
              </div>

              <div className="flex gap-4">
                {Object.values(WeekDay).map((day) => (
                  <div key={day} className="flex items-center gap-2">
                    <Checkbox
                      checked={availableDays.includes(day)}
                      onCheckedChange={(checked: boolean) =>
                        handleChangeAvailableDays(day, checked)
                      }
                    />

                    <label>{day}</label>
                  </div>
                ))}
                {errors.availableDays && (
                  <p className="mt-1 text-sm text-red-500">
                    Please select at least one available day!
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent
              value={ServiceFormTab.DISPLAY}
              forceMount
              className="max-w-4xl space-y-6 data-[state=inactive]:hidden"
            >
              <div className="flex items-center gap-2">
                <h2 className="text-foreground text-lg font-semibold">
                  {t("services.form.displaySettings.title")}
                </h2>
                <PencilLine
                  className="text-muted-foreground size-5 shrink-0"
                  aria-hidden
                />
              </div>

              <div className="border-border overflow-hidden rounded-lg border">
                <div
                  className="border-border bg-muted/40 grid gap-0 border-b"
                  style={{
                    gridTemplateColumns: `minmax(10rem, 1.25fr) repeat(${DISPLAY_STAGE_KEYS.length}, minmax(0, 1fr))`,
                  }}
                >
                  <div className="border-border border-r p-3" />
                  {DISPLAY_STAGE_KEYS.map((stage) => (
                    <div
                      key={stage}
                      className="border-border flex flex-col gap-1 border-r p-3 last:border-r-0"
                    >
                      <span className="text-center text-sm font-medium">
                        {t(
                          `services.form.displaySettings.columns.${stage}.title`,
                        )}
                      </span>
                      <span className="text-muted-foreground text-center text-xs leading-snug">
                        {t(
                          `services.form.displaySettings.columns.${stage}.description`,
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                {(
                  [
                    { type: "row" as const, key: "checkInCheckOut" },
                    { type: "section" as const },
                    { type: "row" as const, key: "carousel" },
                    { type: "row" as const, key: "specialServices" },
                    { type: "row" as const, key: "ctaList" },
                    { type: "row" as const, key: "chatDisplay" },
                  ] as const
                ).map((item) => {
                  if (item.type === "section") {
                    return (
                      <div
                        key="dashboard-section"
                        className="border-border bg-muted/20 border-b px-3 py-2"
                      >
                        <span className="text-sm font-semibold">
                          {t("services.form.displaySettings.rows.dashboard")}
                        </span>
                      </div>
                    );
                  }

                  const rowKey = item.key;
                  const rowState =
                    serviceDisplaySettings?.[rowKey] ??
                    defaultServiceDisplaySettings[rowKey];

                  return (
                    <div
                      key={rowKey}
                      className="border-border grid items-center gap-0 border-b last:border-b-0"
                      style={{
                        gridTemplateColumns: `minmax(10rem, 1.25fr) repeat(${DISPLAY_STAGE_KEYS.length}, minmax(0, 1fr))`,
                      }}
                    >
                      <div className="border-border flex border-r p-3">
                        <span className="border-border bg-background w-full rounded-md border px-3 py-2 text-sm font-medium">
                          {t(`services.form.displaySettings.rows.${rowKey}`)}
                        </span>
                      </div>
                      {DISPLAY_STAGE_KEYS.map((stage) => (
                        <div
                          key={`${rowKey}-${stage}`}
                          className="border-border flex justify-center border-r p-3 last:border-r-0"
                        >
                          <Switch
                            checked={rowState[stage]}
                            onCheckedChange={(checked) =>
                              setGuestStageVisibility(rowKey, stage, checked)
                            }
                          />
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>

          <Button
            type="submit"
            variant="new_default"
            size="xl"
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting
              ? t("services.form.actions.submitting", "Submitting...")
              : submitButtonText}
          </Button>
        </form>
      </div>

      <div className="flex-1 flex-col items-center">
        <h3 className="text-center text-3xl">Guest App Preview</h3>
        <PhonePreview
          selectedImage={selectedImage}
          name={name}
          description={description}
          price={
            price
              ? (
                  parseInt(price) -
                  getPercentageOf(Number(discount) || 0, Number(price))
                ).toString()
              : "0"
          }
          containerClassName="mx-auto mt-10"
        />
      </div>
    </div>
  );
};

export default HotelServiceForm;
